// src/hooks/useTheme.ts
//
// Shared dark-mode hook for the ITSM admin and client portal pages, which
// (unlike App.jsx) are top-level routed pages and don't share App.jsx's
// darkMode state. Unlike the main app's toggle, this one persists to
// localStorage (a gap that exists even in the main app today) and defaults
// to the browser's prefers-color-scheme on first load.
//
// Usage: const { theme, toggleTheme } = useTheme()
// <div className="jr-app" data-theme={theme}>...</div>

import { useEffect, useState, useCallback } from 'react'

export type Theme = 'light' | 'dark'

const STORAGE_KEY = 'jr-theme'

function getInitialTheme(): Theme {
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY)
        if (stored === 'light' || stored === 'dark') return stored
    } catch {
        // localStorage unavailable (private mode, etc.) — fall through to system preference
    }
    try {
        if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark'
    } catch {
        // matchMedia unavailable — default to light
    }
    return 'light'
}

export function useTheme(): { theme: Theme; setTheme: (t: Theme) => void; toggleTheme: () => void } {
    const [theme, setThemeState] = useState<Theme>(getInitialTheme)

    const setTheme = useCallback((t: Theme) => {
        setThemeState(t)
        try { window.localStorage.setItem(STORAGE_KEY, t) } catch { /* best-effort */ }
    }, [])

    const toggleTheme = useCallback(() => {
        setTheme(theme === 'dark' ? 'light' : 'dark')
    }, [theme, setTheme])

    // Keep in sync if the user changes theme in another tab/page.
    useEffect(() => {
        const onStorage = (e: StorageEvent) => {
            if (e.key === STORAGE_KEY && (e.newValue === 'light' || e.newValue === 'dark')) {
                setThemeState(e.newValue)
            }
        }
        window.addEventListener('storage', onStorage)
        return () => window.removeEventListener('storage', onStorage)
    }, [])

    return { theme, setTheme, toggleTheme }
}
